<template>
  <div class="flex">
    <div class="flex items-center" v-if="!editMealPlan">
      <DeleteConfirmation v-model="showDeleteConfirmation" :isSiteConfig="isSiteConfig" />

      <Button
        id="delete-meal-plan"
        severity="danger"
        outlined
        :label="t('sites.singleSite.financialConfig.mealPlans.actions.delete')"
        class="mr-4"
        @click.stop="openDeleteConfirmation"
        v-if="hasMealPlan"
      >
        <template #icon>
          <i class="material-symbols-rounded text-[1.5rem] mr-2">delete</i>
        </template>
      </Button>

      <IconButton id="edit-meal-plan" class="c-icon-button" @click.stop="editMealPlanMethod">
        <template #icon>
          <i class="material-symbols-rounded text-[1.5rem]">edit</i>
        </template>
      </IconButton>
    </div>

    <div class="flex" v-else>
      <Button
        id="cancel-edit-meal-plan"
        outlined
        severity="secondary"
        class="mr-4"
        :disabled="saving"
        :label="t('sites.singleSite.financialConfig.mealPlans.actions.cancel')"
        @click.stop="cancelMealPlanMethod"
      />

      <Button
        id="save-edit-meal-plan"
        :label="t('sites.singleSite.financialConfig.mealPlans.actions.save')"
        :loading="saving"
        @click.stop="saveEditMealPlan"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useEventBus } from '@vueuse/core';
import { useFormStore } from '@centric-os/stores/form/use-form-store';
import { useStoreAPIAction } from '@centric-os/stores/plugins/api';
import { useMealPlan } from '../composables';
import DeleteConfirmation from '../delete-confirmation/DeleteConfirmation.vue';
import { useMealPlanStore } from '../../../../../routes/stores/use-meal-plan-store';
import { useFinancialConfigStore } from '../../../../../routes';
import { FinancialFormIDs } from '../../enums';

// Define props
const props = defineProps({
  isSiteConfig: {
    type: Boolean,
    required: true,
  },
});

// Ref for show delete confirmation
const showDeleteConfirmation = ref<boolean>(false);

// Set localization
const { t } = useI18n();

// Set meal plan store
const mealPlanStore = useMealPlanStore();
const { loading: saving } = useStoreAPIAction(mealPlanStore, 'saveMealPlan');

// Set financial config store
const fConfigStore = useFinancialConfigStore();
const { editMealPlan } = storeToRefs(fConfigStore);

// Set meal plan composable
const { hasMealPlan } = useMealPlan();

// Set form store
const formStore = useFormStore();
const form = formStore.getForm(FinancialFormIDs.MEAL_PLAN_FORM);

// Set edit meal plan method
const editMealPlanMethod = (): void => {
  editMealPlan.value = true;
};

// Cancel edit meal plan method
const cancelMealPlanMethod = (): void => {
  editMealPlan.value = false;
  form.value?.resetForm();
};

// Set event bus for saving meal plan
const { emit: saveEdit } = useEventBus<void>(
  `save-meal-plan-${props.isSiteConfig ? 'site' : 'station'}`,
);

// Save edit meal plan method
const saveEditMealPlan = (): void => {
  saveEdit();
};

// Method to show delete confirmation
const openDeleteConfirmation = (): void => {
  showDeleteConfirmation.value = true;
};
</script>
